<template>
  <div data-testid="uod-branch-context">
    <nav-button :show="isOpen" @toggle="toggleBranchContext" />
    <el-dialog
      v-model="isOpen"
      class="context-select"
      top="5rem"
      lock-scroll
      data-testid="uod-branch-context-dialog"
      :close-on-press-escape="canClose"
      :close-on-click-modal="canClose"
      :show-close="canClose"
    >
      <template #header>
        <span class="context-select__title">
          {{
            $t(headerText || 'nav.main.items.location.select_branch.titlev2')
          }}
        </span>
      </template>
      <el-input
        v-model="searchString"
        :placeholder="$t('nav.main.items.location.search_placeholder')"
        class="mb-2 h-10"
        clearable
        data-testid="uod-branch-context-search"
        @input="filtersChanged"
      />
      <div v-if="!isSearching" class="context-select__link-actions mb-4">
        <button
          v-if="canSelectAllBranches"
          type="button"
          class="context-select__link-action"
          data-testid="uod-branch-context-select-all"
          @click="selectAll"
        >
          {{ $t('nav.main.items.location.select_all') }}
        </button>
        <button
          type="button"
          class="context-select__link-action"
          data-testid="uod-branch-context-clear-all"
          @click="clearAll"
        >
          {{ $t('nav.main.items.location.clear_all') }}
        </button>
      </div>
      <branch-list
        ref="branchList"
        v-loading="loading"
        :table="locationList"
        :infinite-scroll="!!next"
        :can-select-all-branches="canSelectAllBranches"
        :is-searching="isSearching"
        @close="toggleBranchContext"
        @paginate="fetchBranches"
        @selection-change="canApplySelection = $event"
      />
      <template #footer>
        <div class="flex">
          <div class="grow" />

          <el-button
            :disabled="!canClose"
            data-testid="uod-branch-context-cancel"
            @click="toggleBranchContext"
          >
            {{ $t('common.interactions.buttons.cancel') }}
          </el-button>

          <el-button
            v-if="!hideAllLocations"
            type="accent"
            :disabled="!canApplySelection"
            data-testid="uod-branch-context-confirm"
            @click="confirmSelection"
          >
            {{ $t('common.interactions.buttons.select') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import th from '@tillhub/javascript-sdk'
import debounce from 'debounce'
import { useLocationStore } from '@shared/store/location'
import { useAuthStore } from '@shared/store/auth'
import NavButton from './nav-button'
import BranchList from './dialog/branch-list'
import { branchDataFactory, withAccess, getAutoBranchId } from './branches'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export default {
  components: {
    NavButton,
    BranchList
  },
  props: {
    hideAllLocations: {
      type: Boolean,
      default: false
    },
    headerText: {
      type: String,
      default: null
    }
  },
  setup() {
    const locationStore = useLocationStore()
    const { currentLocation } = storeToRefs(locationStore)
    const authStore = useAuthStore()
    const { isAdmin, locations: userAssignedLocations } = storeToRefs(authStore)
    const isSupport = computed(() => !!authStore.isSupport)

    return {
      locationStore,
      currentLocation,
      authStore,
      isAdmin,
      isSupport,
      userAssignedLocations
    }
  },
  data() {
    return {
      loading: false,
      isOpen: false,
      searchString: '',
      branchesList: [],
      next: null,
      canApplySelection: false,
      pendingFilter: false
    }
  },
  computed: {
    branchesData() {
      return this.branchesList.map(branchDataFactory)
    },
    promptForLocation() {
      return (
        !this.currentLocation &&
        !(this.isAdmin || this.isSupport) &&
        this.userIsNowhere
      )
    },
    canClose() {
      return !this.promptForLocation
    },
    userIsNowhere() {
      return (
        Array.isArray(this.userAssignedLocations) &&
        !this.userAssignedLocations.length
      )
    },
    userIsEverywhere() {
      return this.userAssignedLocations === null
    },
    locationList() {
      return withAccess(this.branchesData, this.userAssignedLocations)
    },
    canSelectAllBranches() {
      return this.isAdmin || this.isSupport || this.userIsEverywhere
    },
    isSearching() {
      return !!this.searchString?.trim() || this.pendingFilter
    }
  },
  watch: {
    searchString() {
      // Only user-driven searches (dialog open) gate the bulk actions; the
      // programmatic reset on close must not re-flip pendingFilter via this watcher.
      if (this.isOpen) this.pendingFilter = true
    },
    isOpen(status) {
      if (status) {
        this.$nextTick(() => {
          this.$refs.branchList?.resetUserSelectionState?.()
        })
        this.fetchBranches()
      } else {
        this.next = null
        this.branchesList = []
        this.searchString = ''
        this.pendingFilter = false
      }
    }
  },
  async created() {
    await this.authStore.waitThInitializated()
    this.locationStore.fetchMerchantFeatures()
    await this.fetchBranches()
    this.autoSetBranch()
    if (this.promptForLocation) {
      this.isOpen = true
    }
  },
  methods: {
    autoSetBranch() {
      const branchId = getAutoBranchId({
        isPrivileged: this.isAdmin || this.isSupport,
        hasSelection: !!this.currentLocation,
        branchesData: this.branchesData,
        locationList: this.locationList,
        userAssignedLocations: this.userAssignedLocations
      })
      if (branchId) this.selectBranch(branchId)
    },
    toggleBranchContext() {
      this.isOpen = !this.isOpen
    },
    selectBranch(branchId) {
      const branchData = this.getBranch(branchId)
      if (!branchData) return
      this.locationStore.setLocationContext({
        branches: [branchData],
        location: branchId
      })
    },
    getBranch(id) {
      const branch = this.branchesList.find((branch) => branch.id === id)
      return branchDataFactory(branch)
    },
    confirmSelection() {
      this.$refs.branchList?.applySelection?.()
    },
    selectAll() {
      this.$refs.branchList?.selectAll?.()
    },
    clearAll() {
      this.$refs.branchList?.clearAll?.()
    },
    filtersChanged: debounce(function () {
      this.branchesList = []
      this.next = null
      this.fetchBranches()
    }, 300),
    async fetchBranches() {
      this.loading = true
      const syncLocationStore = !this.isOpen
      const requestedSearch = this.searchString
      try {
        if (this.next) await this.paginate()
        else if (this.isAdmin || this.isSupport || this.userIsEverywhere) {
          await this.fetchPaginatedBranches(syncLocationStore)
        } else await this.fetchAllBranches(syncLocationStore)
      } catch (err) {
        this.$logException(err, {
          trackError: false,
          message: this.$t('common.error.action.read.multiple', {
            resources: this.$t('common.resource.branch.plural')
          })
        })
      } finally {
        this.loading = false
        // A fetch whose search has since changed is stale; leave pendingFilter
        // set so the bulk actions stay hidden until the current search settles.
        if (this.searchString === requestedSearch) {
          this.pendingFilter = false
        }
      }
    },
    async syncFetchedBranchesToLocationStore(branches) {
      try {
        if (branches.length === 1) {
          this.locationStore.locationUnzerId = branches[0]?.mms_unit_unzer_id
        }
        this.locationStore.locationContextAll = { branches }
      } catch (err) {
        this.$logException(err, { trackError: false })
      }
    },
    async fetchPaginatedBranches(syncLocationStore = true) {
      const { data = [], next } = await th.branchesV1().getAll({
        query: {
          limit: 50,
          deleted: false,
          q: this.searchString,
          extended: 'true'
        }
      })
      this.next = next
      this.branchesList = data

      if (syncLocationStore) {
        this.syncFetchedBranchesToLocationStore(data)
      }
    },
    async fetchAllBranches(syncLocationStore = true) {
      let branchesV1 = []
      try {
        const result = await this.$resourceFetch({
          resource: 'branchesV1',
          query: {
            deleted: false,
            q: this.searchString,
            extended: 'true'
          }
        })
        branchesV1 = result.branchesV1 || []
      } catch (err) {
        this.$logException(err, { trackError: false })
        this.branchesList = []
        return
      }

      this.branchesList = branchesV1

      if (syncLocationStore) {
        this.syncFetchedBranchesToLocationStore(branchesV1)
      }

      return this.branchesList
    },
    async paginate() {
      if (!this.next) return
      const { data = [], next } = await this.next()
      this.next = next
      this.branchesList = this.branchesList.concat(data)
    }
  }
}
</script>

<style scoped>
.context-select :deep(.el-dialog__header) {
  text-align: left;
  margin-right: 0;
}

.context-select__title {
  color: var(--Palette-Plum-Plum-100, #0c1332);
  font-family: 'Unzer Visuelt', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.64px;
}

.context-select :deep(.el-dialog__body) {
  overflow: hidden;
}

.context-select__link-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.context-select__link-action {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--Palette-Blueberry-Blueberry-60, #1b6ad7);
  font-family: 'Unzer Visuelt', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.7px;
}

.context-select__link-action:hover {
  color: var(--el-color-primary-light-3, #66b1ff);
  text-decoration: underline;
}
</style>
